import { useMemo } from 'react';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { useUser, useUserId } from '../../hooks/useUser';
import { useClientId } from '../../hooks/useClientId';
import { useCurrentUser, type DocumentInfo } from '../../api/endpoints/useCurrentUser';
import { useEntityDetails } from '../../api/endpoints/useEntityDetails';
import { useEntityMetadata } from '../../api/endpoints/useEntityMetadata';
import { useUpdateUser } from '../../api/endpoints/useUpdateUser';
import {
  extractTimezoneOptions,
  extractLocaleOptions,
  getDefaultTimezone,
  getDefaultLocale,
} from './schemaUtils';
import { UPDATE_PROFILE_ACTION_REFERENCE } from './constants';

/**
 * Custom hook to fetch and process user profile data
 */
export function useUserProfile() {
  const { t } = useTranslation();
  const { user } = useUser();
  const clientId = useClientId();
  const currentUserId = useUserId();
  const { data: meData } = useCurrentUser(clientId);
  const { data: entityDetails, refetch: refetchEntityDetails } =
    useEntityDetails(clientId, 'user', currentUserId);
  const { data: metadataWithSchema } = useEntityMetadata(clientId, 'user', {
    includeResolvedSchema: true,
  });
  const updateUser = useUpdateUser(
    clientId,
    'user',
    currentUserId,
    t('userDetails.profileUpdateSuccess')
  );

  const personnel =
    entityDetails?.data?.data?.entityData || meData?.data?.entityData;

  // Get the update action ID from entity details by reference
  const updateActionId = useMemo(() => {
    const actions = entityDetails?.data?.actions || [];
    const updateAction = actions.find(
      action => action.reference === UPDATE_PROFILE_ACTION_REFERENCE
    );
    return updateAction?.id || null;
  }, [entityDetails]);
  const fullName = personnel?.name || user?.name || '';
  const emailAddress =
    personnel?.profileInformation?.emailAddress || user?.email || '';
  const photo = personnel?.profileInformation?.photo;

  const configuredTimezone =
    personnel?.preferences?.timezone || getDefaultTimezone();
  const configuredLocale =
    personnel?.preferences?.localeIdentifier || getDefaultLocale();

  const timezoneOptions = useMemo(
    () => extractTimezoneOptions(metadataWithSchema),
    [metadataWithSchema]
  );

  const localeOptions = useMemo(
    () => extractLocaleOptions(metadataWithSchema),
    [metadataWithSchema]
  );

  const handleUpdateTimezone = async (timezone: string) => {
    if (!updateActionId || !personnel) return;

    // Send the complete user object with updated timezone
    await updateUser.mutateAsync({
      actionId: updateActionId,
      variables: {
        ...personnel,
        preferences: {
          ...personnel.preferences,
          timezone,
          localeIdentifier: configuredLocale,
        },
      },
    });
    await refetchEntityDetails();
    // Reload the page to apply timezone changes
    window.location.reload();
  };

  const handleUpdateLocale = async (locale: string) => {
    if (!updateActionId || !personnel) return;

    // Send the complete user object with updated locale
    await updateUser.mutateAsync({
      actionId: updateActionId,
      variables: {
        ...personnel,
        preferences: {
          ...personnel.preferences,
          timezone: configuredTimezone,
          localeIdentifier: locale,
        },
      },
    });
    await refetchEntityDetails();
    // Reload the page to apply locale changes
    window.location.reload();
  };

  const handleUpdatePhoto = async (photo: DocumentInfo | null) => {
    if (!updateActionId || !personnel) return;

    // Send the complete user object with updated photo
    await updateUser.mutateAsync({
      actionId: updateActionId,
      variables: {
        ...personnel,
        profileInformation: {
          ...personnel.profileInformation,
          photo,
        },
      },
    });
    await refetchEntityDetails();
    // Reload the page to reflect the photo update
    window.location.reload();
  };

  return {
    fullName,
    emailAddress,
    photo,
    configuredTimezone,
    configuredLocale,
    timezoneOptions,
    localeOptions,
    handleUpdateTimezone,
    handleUpdateLocale,
    handleUpdatePhoto,
    isUpdating: updateUser.isPending || false,
    canUpdate: updateActionId !== null,
    clientId,
  };
}
